/**
 * Production adapter for the Contour+ job tools: binds the React-free ports
 * to the viewer's existing job handling — the job queue (`jobQueueState`,
 * kept current by `useConnectorJobOrchestrator`'s polling), the connector's
 * generated fetchers it already uses (`fetchGetTaskStatus`, `downloadErrors`)
 * and the Job Queue panel's Cancel steps (`setJobStatus` then
 * `fetchCancelTask` in a connector session, or the backend client's abort).
 * Like the other ops' engineDeps, this browser glue is covered by the manual
 * demo; the logic it feeds is unit-tested through fakes.
 */
import { useCallback } from 'react';
import { useRecoilValue } from 'recoil';
import {
    fetchCancelTask,
    fetchGetTaskStatus,
} from '~/backend/connector-api/connector-components';
import { downloadErrors } from '~/backend/connector-api/utils';
import { checkIsInSessionRouteWithoutRouter } from '~/hooks/session/useIsInSessionRoute';
import { useJobManagement } from '~/hooks/workflows/useJobManagement';
import { isJobRunning, jobQueueState, type Job } from '~/state/atoms/jobAtoms';
import { WorkflowTypes } from '~/state/atoms/viewerAtoms';
import AppConfig from '~/utils/appconfig';
import type { CancelJobDeps } from './cancelToolRunner';
import type { ContourJobFacts, ContourJobReadDeps } from './jobQueries';

function toFacts(job: Job): ContourJobFacts {
    return {
        jobId: job.id,
        model: job.task?.model?.name ?? '',
        status: job.status,
        ...(job.error ? { error: job.error } : {}),
        createdAt: job.createdAt,
        ...(job.finishedAt !== undefined ? { finishedAt: job.finishedAt } : {}),
        isImported: job.isImported ?? false,
    };
}

export type ContourJobEngineDeps = ContourJobReadDeps &
    Omit<CancelJobDeps, 'pendingMutationExists'>;

export function useContourJobDeps(): () => ContourJobEngineDeps {
    const jobQueue = useRecoilValue(jobQueueState);
    const { setJobStatus } = useJobManagement();

    return useCallback((): ContourJobEngineDeps => {
        const contourJobs = () =>
            jobQueue.jobs
                .filter((j) => j.type === WorkflowTypes.CONTOUR)
                .map(toFacts);
        return {
            connectorMode: AppConfig.isConnectorMode,
            contourJobs,
            taskStatus: async (jobId) => {
                const task = await fetchGetTaskStatus({
                    pathParams: { taskID: jobId },
                });
                return {
                    status: task.status,
                    errorFiles: (task.errors ?? [])
                        .map((e) => e.filename)
                        .filter((f): f is string => !!f),
                };
            },
            readErrors: async (jobId, files) => {
                const outcome = await downloadErrors(jobId, files);
                return Promise.all(outcome.files.map((f) => f.text()));
            },
            isRunning: (status) => isJobRunning(status as Job['status']),
            // The Job Queue panel's Cancel button, step for step.
            cancel: async (jobId) => {
                const job = jobQueue.jobs.find((j) => j.id === jobId);
                setJobStatus(jobId, 'Canceled', 'Cancelled');
                if (checkIsInSessionRouteWithoutRouter()) {
                    await fetchCancelTask({ pathParams: { taskID: jobId } });
                } else if (job?.client) {
                    job.client.abort();
                }
            },
        };
    }, [jobQueue, setJobStatus]);
}
